import { Check, Close, Visibility, VisibilityOff } from '@mui/icons-material'
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
} from '@mui/material'
import Alert from '@mui/material/Alert'
import {
  groupingInvoiceTypeName,
  invoiceTypeName,
  scheduleTypeName,
} from 'api/models/Common/Invoice.type'
import { CountryCode } from 'api/models/GetAddress.type'
import { CountrySelect } from 'components/Common/CountrySelect/CountrySelect'
import notifyPopup from 'components/Common/Notification/notifyPopup'
import RadioSwitch from 'components/FormFields/RadioSwitch/RadioSwitch'
import Select from 'components/FormFields/Select/Select'
import Form from 'components/RouteWizard/Form'
import useClient from 'hooks/useClient'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getBookingReferences } from 'reducers/clientsReducer'
import Input from '../../../FormFields/Input/Input'
import styles from './billingStyle'
import schema from './schema'

function Section({ title, children, sx }) {
  return (
    <Paper variant="outlined" sx={{ ...styles.section, ...sx }}>
      <Box sx={styles.sectionHeader}>
        <FormLabel sx={styles.sectionTitle}>{title}</FormLabel>
      </Box>
      <Box sx={styles.sectionBody}>{children}</Box>
    </Paper>
  )
}

Section.propTypes = {
  title: PropTypes.node.isRequired,
  children: PropTypes.node,
  sx: PropTypes.object,
}

function Billing(props) {
  const client = { ...props.client }
  const { updateClient, updateClientInvoicing, isLoading, error } = useClient()
  const dispatch = useDispatch()
  const [errors, setErrors] = useState({})
  const initialData = { ...client }
  const [formData, setFormData] = useState(initialData)
  const [invoiceData, setInvoiceData] = useState(
    initialData.invoiceSettings || {},
  )
  const [editForm, setEditForm] = useState(false)
  const [enableGrouping, setEnableGrouping] = useState(
    initialData.invoiceSettings &&
      initialData.invoiceSettings.groupingInvoiceTypeName !== undefined,
  )
  const [allBookingReferences, setAllBookingReferences] = useState([])
  const [showAccountingRef, setShowAccountingRef] = useState(false)

  const getBookingReferencesForClient = (clientId) => {
    const bookingReferences = []
    dispatch(getBookingReferences(clientId)).then((result) => {
      if (!result.error) {
        result.payload.forEach((ref) => {
          bookingReferences.push(ref)
        })
        setAllBookingReferences(bookingReferences)
      }
    })
  }

  function setDeepValue(obj, path, value) {
    const keys = path.split('.')
    const [firstKey, ...restKeys] = keys
    if (restKeys.length === 0) {
      return { ...obj, [firstKey]: value }
    }
    return {
      ...obj,
      [firstKey]: setDeepValue(obj[firstKey] || {}, restKeys.join('.'), value),
    }
  }

  const handleCancel = () => {
    setEditForm(false)
    setFormData(initialData)
    setInvoiceData(initialData.invoiceSettings || {})
    setErrors({})
  }

  const onChange = (eOrValue) => {
    let fieldName = null

    if (eOrValue && eOrValue.target) {
      const { name, value } = eOrValue.target
      fieldName = name
      setFormData((prev) => setDeepValue({ ...prev }, name, value))
    } else if (
      eOrValue &&
      eOrValue.name !== undefined &&
      eOrValue.value !== undefined
    ) {
      fieldName = eOrValue.name
      setFormData((prev) =>
        setDeepValue({ ...prev }, eOrValue.name, eOrValue.value),
      )
    } else if (typeof eOrValue === 'string') {
      fieldName = 'billing.address.countryCode'
      let updated = setDeepValue(
        { ...formData },
        'billing.address.countryCode',
        eOrValue,
      )
      updated = setDeepValue(
        updated,
        'billing.address.country',
        CountryCode[eOrValue] || '',
      )
      setFormData(updated)
    }

    // Clear error for this field
    if (fieldName && errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: undefined }))
    }
  }

  const onInvoiceChange = (eOrValue) => {
    let fieldName = null

    if (eOrValue && eOrValue.target) {
      const { name, value } = eOrValue.target
      fieldName = name
      setInvoiceData((prev) => setDeepValue({ ...prev }, name, value))
    } else if (
      eOrValue &&
      eOrValue.name !== undefined &&
      eOrValue.value !== undefined
    ) {
      fieldName = eOrValue.name
      setInvoiceData((prev) =>
        setDeepValue({ ...prev }, eOrValue.name, eOrValue.value),
      )
    }
    // Clear error for this field
    if (fieldName && errors[fieldName]) {
      setErrors((prev) => ({ ...prev, [fieldName]: undefined }))
    }
  }

  useEffect(() => {
    getBookingReferencesForClient(client.id)
  }, [])

  const switchToParentClientBilling = () => {
    updateClient(
      { data: { billing: {}, settings: { isBilledFromParent: true } } },
      client.id,
    ).then(() => {
      notifyPopup({
        type: error ? 'danger' : 'success',
        message: error
          ? 'Update failed to save'
          : 'Details updated successfully',
      })
      setFormData((prev) =>
        setDeepValue({ ...prev }, 'settings.isBilledFromClient', true),
      )
    })
  }

  const onSubmit = () => {
    setEditForm(false)
    updateClient({ data: { billing: formData.billing } }, client.id).then(
      () => {
        notifyPopup({
          type: error ? 'danger' : 'success',
          message: error
            ? 'Update failed to save'
            : 'Details updated successfully',
        })
      },
    )
    updateClient(
      { data: { settings: { isBilledFromParent: false } } },
      client.id,
    ).then(() => {
      notifyPopup({
        type: error ? 'danger' : 'success',
        message: error
          ? 'Update failed to save'
          : 'Details updated successfully',
      })
    })
    updateClientInvoicing(
      { data: invoiceData },
      client.id,
      formData.invoiceSettings,
    ).then(() => {
      notifyPopup({
        type: error ? 'danger' : 'success',
        message: error
          ? 'Update failed to save'
          : 'Details updated successfully',
      })
    })
  }

  const externalReference = invoiceData.externalReference || ''
  const maskExternalReference = (value) =>
    value.length <= 4
      ? value
      : '•'.repeat(value.length - 4).concat(value.slice(-4))

  return !formData?.settings.isBilledFromParent &&
    formData.platform.platformInvoiceTypeName === 'Client' ? (
    <Form
      schema={schema}
      onSubmit={onSubmit}
      defaultValues={formData.billing}
      showControls={false}
    >
      <Box sx={styles.componentPanel}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Section title="Payment Details" sx={{ height: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <Input
                    label="Account Holder"
                    name="billing.payment.accountHolderName"
                    value={formData.billing?.payment?.accountHolderName || ''}
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Input
                    label="Account Number"
                    name="billing.payment.accountNumber"
                    value={formData.billing?.payment?.accountNumber || ''}
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Input
                    label="Sort Code"
                    name="billing.payment.sortCode"
                    value={formData.billing?.payment?.sortCode || ''}
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                  />
                </Grid>
              </Grid>
            </Section>
          </Grid>
          <Grid item xs={12} md={4}>
            <Section title="VAT" sx={{ height: '100%' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <FormControl sx={styles.toggleControl} disabled={!editForm}>
                    <RadioSwitch
                      name="billing.vatRegistered"
                      options={[
                        { value: 'true', label: <Check /> },
                        { value: 'false', label: <Close /> },
                      ]}
                      disabled={!editForm}
                      onChange={(e) => {
                        if (editForm) {
                          onChange({
                            name: 'billing.vatRegistered',
                            value: e.target.value === 'true',
                          })
                        }
                      }}
                      displayMode="compact"
                      value={String(formData.billing?.vatRegistered)}
                      label={<>VAT Registered</>}
                    />
                  </FormControl>
                </Grid>
                {formData.billing?.vatRegistered && (
                  <Grid item xs={12}>
                    <Input
                      label="VAT Number"
                      name="billing.vatNumber"
                      value={formData.billing?.vatNumber || ''}
                      onChange={onChange}
                      InputLabelProps={{ shrink: true }}
                      disabled={!editForm}
                    />
                  </Grid>
                )}
              </Grid>
            </Section>
          </Grid>
        </Grid>

        <Section title="Invoicing & Payment Terms">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Select
                id="invoiceTypeName"
                disabled={!editForm}
                label="Invoice Type"
                name="invoiceTypeName"
                value={invoiceData.invoiceTypeName || ''}
                onChange={onInvoiceChange}
              >
                {Object.entries(invoiceTypeName).map((type, idx) => {
                  const isManaged = formData.settings.isManaged
                  if (
                    !type[0].includes('Platform') &&
                    !type[0].includes(!isManaged ? 'Managed' : 'Agent')
                  )
                    return (
                      <MenuItem key={idx} value={type[0]}>
                        {type[1]}
                      </MenuItem>
                    )
                })}
              </Select>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Select
                id="scheduleTypeName"
                disabled={!editForm}
                label="Invoice Schedule"
                name="scheduleTypeName"
                value={invoiceData.scheduleTypeName || ''}
                onChange={onInvoiceChange}
              >
                {Object.entries(scheduleTypeName).map((schedule, idx) => {
                  return (
                    <MenuItem key={idx} value={schedule[0]}>
                      {schedule[1]}
                    </MenuItem>
                  )
                })}
              </Select>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Input
                label="Accounting Reference"
                name="externalReference"
                autoComplete="off"
                value={
                  editForm || showAccountingRef
                    ? externalReference
                    : maskExternalReference(externalReference)
                }
                onChange={onInvoiceChange}
                InputLabelProps={{ shrink: true }}
                disabled={!editForm}
                InputProps={
                  editForm
                    ? undefined
                    : {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle accounting reference visibility"
                              onClick={() =>
                                setShowAccountingRef((show) => !show)
                              }
                              edge="end"
                              size="small"
                            >
                              {showAccountingRef ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Select
                id="paymentTermSelect"
                disabled={!editForm}
                label="Payment Term"
                name="billing.billingTermTypeName"
                value={formData.billing?.billingTermTypeName || ''}
                onChange={onChange}
              >
                <MenuItem value={'EndOfMonth'}>
                  End of month following date of invoice
                </MenuItem>
                <MenuItem value={'DaysFromInvoice'}>
                  Days from date of invoice
                </MenuItem>
              </Select>
            </Grid>
            {formData.billing?.billingTermTypeName === 'DaysFromInvoice' && (
              <Grid item xs={12} sm={6}>
                <Input
                  label="Number of Days"
                  name="billing.billingTermValue"
                  onChange={onChange}
                  disabled={!editForm}
                  value={formData.billing?.billingTermValue || ''}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Input
                id="startDate"
                label="Invoice Start Date"
                name="startDate"
                inputProps={{ type: 'date' }}
                value={
                  invoiceData.startDate
                    ? moment(invoiceData.startDate).format('YYYY-MM-DD')
                    : ''
                }
                onChange={onInvoiceChange}
                InputLabelProps={{ shrink: true }}
                disabled={!editForm}
              />
            </Grid>
            {allBookingReferences.length > 0 && (
              <Grid item xs={12}>
                <FormControl sx={styles.toggleControl} disabled={!editForm}>
                  <RadioSwitch
                    options={[
                      { value: 'true', label: <Check /> },
                      { value: 'false', label: <Close /> },
                    ]}
                    disabled={!editForm}
                    onChange={(e) => {
                      if (editForm) {
                        setEnableGrouping(e.target.value === 'true')
                      }
                    }}
                    displayMode="compact"
                    value={String(enableGrouping || 'false')}
                    label={<>Enable Invoice Grouping</>}
                  />
                </FormControl>
              </Grid>
            )}
            {editForm && enableGrouping && (
              <Grid item xs={12} sm={6}>
                <Select
                  id="groupingReferenceId"
                  disabled={!editForm}
                  label="Group Invoice By"
                  name="groupingReferenceId"
                  value={invoiceData.groupingReferenceId || ''}
                  onChange={onInvoiceChange}
                >
                  {allBookingReferences.map((ref, idx) => {
                    return (
                      <MenuItem key={idx} value={ref.bookingReferenceId}>
                        {ref.title}
                      </MenuItem>
                    )
                  })}
                </Select>
              </Grid>
            )}
            {editForm && enableGrouping && (
              <Grid item xs={12} sm={6}>
                <Select
                  id="groupingInvoiceTypeName"
                  disabled={!editForm}
                  label="Group Type"
                  name="groupingInvoiceTypeName"
                  value={invoiceData.groupingInvoiceTypeName || ''}
                  onChange={onInvoiceChange}
                >
                  {Object.entries(groupingInvoiceTypeName).map(
                    (schedule, idx) => {
                      return (
                        <MenuItem key={idx} value={schedule[0]}>
                          {schedule[1]}
                        </MenuItem>
                      )
                    },
                  )}
                </Select>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl sx={styles.toggleControl} disabled={!editForm}>
                <RadioSwitch
                  name="isVatable"
                  options={[
                    { value: 'true', label: <Check /> },
                    { value: 'false', label: <Close /> },
                  ]}
                  disabled={!editForm}
                  onChange={(e) => {
                    if (editForm) {
                      onInvoiceChange({
                        name: 'isVatable',
                        value: e.target.value === 'true',
                      })
                    }
                  }}
                  displayMode="compact"
                  value={String(invoiceData.isVatable || 'false')}
                  label={<>VAT Applicable</>}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl sx={styles.toggleControl} disabled={!editForm}>
                <RadioSwitch
                  name="shouldSendEmail"
                  options={[
                    { value: 'true', label: <Check /> },
                    { value: 'false', label: <Close /> },
                  ]}
                  disabled={!editForm}
                  onChange={(e) => {
                    if (editForm) {
                      onInvoiceChange({
                        name: 'shouldSendEmail',
                        value: e.target.value === 'true',
                      })
                    }
                  }}
                  displayMode="compact"
                  value={String(invoiceData.shouldSendEmail || 'false')}
                  label={<>Send by email</>}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Section>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Section title="Billing Address" sx={{ height: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Input
                    label="Billing Name"
                    name="billing.entity"
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                    value={formData.billing?.entity || ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Input
                    label="Street Address"
                    name="billing.address.addressLine1"
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                    value={formData.billing?.address?.addressLine1 || ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Input
                    label="Street Address 2"
                    name="billing.address.addressLine2"
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                    value={formData.billing?.address?.addressLine2 || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Input
                    label="Town"
                    name="billing.address.town"
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                    value={formData.billing?.address?.town || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Input
                    label="County"
                    name="billing.address.county"
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                    value={formData.billing?.address?.county || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Input
                    label="Post / Zip Code"
                    name="billing.address.postCode"
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                    value={formData.billing?.address?.postCode || ''}
                  />
                </Grid>
                <Grid item xs={12} sx={{ display: 'none' }}>
                  <Input
                    label="Country"
                    name="billing.address.country"
                    disabled={!editForm}
                    value={formData.billing?.address?.country || ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CountrySelect
                    label="Country"
                    name="billing.address.countryCode"
                    onChange={onChange}
                    disabled={!editForm}
                    value={formData.billing?.address?.countryCode}
                    sibling="billing.address.country"
                    width="100%"
                  />
                </Grid>
              </Grid>
            </Section>
          </Grid>
          <Grid item xs={12} md={6}>
            <Section title="Billing Contact" sx={{ height: '100%' }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Input
                    label="Contact Name"
                    name="billing.contact.name"
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                    value={formData.billing?.contact?.name || ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Input
                    label="Contact Email"
                    name="billing.contact.email"
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                    value={formData.billing?.contact?.email || ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Input
                    label="Contact Number"
                    name="billing.contact.contactNumber"
                    onChange={onChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!editForm}
                    value={formData.billing?.contact?.contactNumber || ''}
                  />
                </Grid>
              </Grid>
            </Section>
          </Grid>
        </Grid>

        <Box sx={styles.actions}>
          {!formData?.settings.isBilledFromParent &&
          formData.parentClientId != null ? (
            <Button color={'primary'} onClick={switchToParentClientBilling}>
              Set Billing from Parent Client
            </Button>
          ) : (
            <></>
          )}
          <Button
            color={!editForm ? 'primary' : 'error'}
            onClick={() => {
              if (editForm) {
                handleCancel()
              } else {
                setEditForm(true)
              }
            }}
          >
            {!editForm ? 'Edit' : 'Cancel'}
          </Button>
          {editForm && (
            <Button type="submit" color="success" disabled={isLoading}>
              Save
            </Button>
          )}
        </Box>
      </Box>
    </Form>
  ) : (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        width: '100%',
        gap: '20px',
      }}
    >
      <Alert severity="info">
        Billing settings are set by the parent{' '}
        {client.parentClient?.companyName
          ? `client: ${client.parentClient.companyName}`
          : 'platform'}
        . Click below to set billing information directly against this client.
      </Alert>

      <Button
        onClick={() => {
          setEditForm(true)
          setFormData((prev) => ({
            ...prev,
            billing: {
              ...prev.billing,
              payment: {},
              address: {},
              contact: {},
              entity: '',
              billingTermTypeName: '',
              billingTermValue: '',
              vatNumber: '',
              vatRegistered: false,
            },
            settings: { ...prev.settings, isBilledFromParent: false },
          }))
        }}
      >
        Set billing information
      </Button>
    </Box>
  )
}

Billing.propTypes = {
  client: PropTypes.object.isRequired,
}

export default Billing
